import type {
	ChainOptions,
	JSResourceOptions,
	Loader,
	PreloadableJSResourceReference,
} from '../../types';
import { createCache } from '../../utils/create-cache';
import { PreloadableJSResource } from '../preloadable-js-resource';
import { postTask } from '../scheduler';

const cache = createCache(new Map<string, PreloadableJSResourceReference<any>>());

/** Message when a string path reaches runtime (expected: build rewrites it to a loader). */
const RUNTIME_STRING_ERROR =
	'JSResourceForInteraction was called with a string path at runtime. ' +
	'Ensure @atlassian/react-async-transformer is applied to transform this to a dynamic import.';

export function JSResourceForInteraction<T = unknown>(
	path: string,
	opts?: JSResourceOptions & ChainOptions<T>,
): PreloadableJSResourceReference<T>;

export function JSResourceForInteraction<T = unknown>(
	loader: Loader<T>,
	opts?: JSResourceOptions & ChainOptions<T>,
): PreloadableJSResourceReference<T>;

export function JSResourceForInteraction<T = unknown>(
	loaderOrPath: Loader<T> | string,
	opts?: JSResourceOptions & ChainOptions<T>,
): PreloadableJSResourceReference<T> {
	if (typeof loaderOrPath === 'string') {
		throw new Error(`${RUNTIME_STRING_ERROR} String path: ${JSON.stringify(loaderOrPath)}`);
	}
	const { chain } = opts ?? {};
	return cache.get(opts!.moduleId!, () =>
		PreloadableJSResource(
			() =>
				postTask(chain ? () => chain(Promise.resolve(loaderOrPath())) : loaderOrPath, {
					priority: 'user-visible',
				}),
			opts!,
		),
	);
}
